import React from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { TestimonialSection } from './components/testimonials/TestimonialSection';
import { PricingPage } from './components/pricing/PricingPage';
import { InvoiceList } from './components/invoices/InvoiceList';
import { PayslipList } from './components/payroll/PayslipList';
import { JobQuotes } from './components/jobs/JobQuotes';
import { ExpenseTracker } from './components/expenses/ExpenseTracker';
import { EquipmentManager } from './components/equipment/EquipmentManager';
import { TimeSheets } from './components/timesheet/TimeSheets';
import { AIInsightsPanel } from './components/ai/AIInsightsPanel';
import { DocumentScanner } from './components/premium/DocumentScanner';
import { FinancialForecasting } from './components/premium/FinancialForecasting';
import { SmartReceiptAnalyzer } from './components/ai/SmartReceiptAnalyzer';
import { SmartFinancialAssistant } from './components/ai/SmartFinancialAssistant';
import { SmartContractAnalyzer } from './components/ai/SmartContractAnalyzer';
import { CustomerSupportAssistant } from './components/ai/CustomerSupportAssistant';
import { Tutorial } from './components/onboarding/Tutorial';
import { AuthGate } from './components/auth/AuthGate';
import { Footer } from './components/Footer';
import { Info } from 'lucide-react';
import { MOCK_DATA } from './lib/utils/constants';

function App() {
  const [showTutorial, setShowTutorial] = React.useState(true);

  const handleDismissTutorial = React.useCallback(() => {
    setShowTutorial(false);
    localStorage.setItem('tutorialDismissed', 'true');
  }, []);

  React.useEffect(() => {
    const tutorialDismissed = localStorage.getItem('tutorialDismissed');
    if (tutorialDismissed) {
      setShowTutorial(false);
    }
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg text-dark-text">
      <Header />
      <main id="main-content" role="main" aria-label="Main content">
        <Hero />
        <TestimonialSection />
        <PricingPage />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          {showTutorial && <Tutorial onDismiss={handleDismissTutorial} />}

          <div className="ai-insights">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-2xl font-bold text-dark-text">AI-Powered Features</h2>
              <Info className="h-5 w-5 text-primary-400" aria-hidden="true" />
            </div>
            <AuthGate title="AI Financial Insights Dashboard">
              <AIInsightsPanel financialData={MOCK_DATA.financial} expenses={MOCK_DATA.expenses} />
            </AuthGate>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="receipt-analyzer">
              <AuthGate title="Smart Receipt Analysis">
                <SmartReceiptAnalyzer />
              </AuthGate>
            </div>
            <AuthGate title="AI Financial Assistant">
              <SmartFinancialAssistant />
            </AuthGate>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="contract-analyzer">
              <AuthGate title="Smart Contract Analysis">
                <SmartContractAnalyzer />
              </AuthGate>
            </div>
            <div className="support-assistant">
              <AuthGate title="AI Support Assistant">
                <CustomerSupportAssistant />
              </AuthGate>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AuthGate title="Document Scanner & Analysis">
              <DocumentScanner />
            </AuthGate>
            <AuthGate title="AI Financial Forecasting">
              <FinancialForecasting />
            </AuthGate>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AuthGate title="Smart Job Quote Management">
              <JobQuotes />
            </AuthGate>
            <AuthGate title="Expense Tracking & Analysis">
              <ExpenseTracker />
            </AuthGate>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AuthGate title="Equipment Management System">
              <EquipmentManager />
            </AuthGate>
            <AuthGate title="Smart Time Tracking">
              <TimeSheets />
            </AuthGate>
          </div>

          <AuthGate title="Intelligent Invoice Management">
            <InvoiceList />
          </AuthGate>
          <AuthGate title="Smart Payroll System">
            <PayslipList />
          </AuthGate>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default App;